import { FormControlLabel, Switch } from "@mui/material";
import { FormikProps } from "formik";
import { useEffect, useState } from "react";
import CustomTextArea from "../../../../../../../../../../../../components/CustomTextArea";
import Flex from "../../../../../../../../../../../../components/Flex";
import Text from "../../../../../../../../../../../../components/Text";
import StyledButtonV2 from "../../../../../../../../../../../../components/app/StyledButtonV2";
import StyledFormModal from "../../../../../../../../../../../../components/globalModal/StyledModal";
import { DisclosureHost } from "../../../../../../../../../../../../hooks/use-disclosure";
import useFormDraft from "../../../../../../../../../../../../hooks/use-form-draft";
import { Form2Payload } from "../../../../../../../../../scheduling/appointment/encounter/encounter-constant";
import CheckboxCardSections from "../CheckboxCardSections";
import { PHYSICAL_ASSESSMENT_EXAM_SECTIONS } from "../InitialPsychotherapyConstant";

interface MentalStatusExaminationModalProps {
  disclosureHost: DisclosureHost;
  formik: FormikProps<Form2Payload>;
}

// Options selected when "Mark as Normal" is toggled on.
const PHYSICAL_ASSESSMENT_NORMAL_OPTIONS: Record<string, string[]> = {
  appearance: [
    "cleanGroomed",
    "dressedAppropriatelyForClimate",
    "appearsStatedAge",
    "wellNourished",
    "normalBMI",
    "wellHydrated",
  ],
  behavior: ["cooperative", "calmRelaxed", "eyeContactNormal"],
  speech: ["speakingNormalRateAndVolume"],
};

// Fields this modal owns: every section selection, its note, and the overall note.
const MSE_FIELDS = [
  ...PHYSICAL_ASSESSMENT_EXAM_SECTIONS.flatMap((section) => [
    section.key,
    section.noteKey ?? `${section.key}Note`,
  ]),
  "overallMseNote",
];

const MentalStatusExaminationModal = ({
  disclosureHost,
  formik,
}: MentalStatusExaminationModalProps) => {
  const [markAsNormal, setMarkAsNormal] = useState(false);

  // Edits stay in the draft until Save, so the note behind the modal is
  // untouched while the user is still filling it in (ECRH-5663).
  const draft = useFormDraft(formik, MSE_FIELDS, disclosureHost.opened);

  useEffect(() => {
    if (disclosureHost.opened) setMarkAsNormal(false);
  }, [disclosureHost.opened]);

  const handleMarkAsNormalChange = (checked: boolean) => {
    setMarkAsNormal(checked);
    PHYSICAL_ASSESSMENT_EXAM_SECTIONS.forEach((section) => {
      draft.setFieldValue(
        section.key,
        checked
          ? (PHYSICAL_ASSESSMENT_NORMAL_OPTIONS[section.key] || []).join(",")
          : ""
      );
    });
  };

  const handleSave = () => {
    draft.save();
    disclosureHost.close();
  };

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={() => disclosureHost.close()}
      dialogTitle="Mental Status Examination (MSE)"
      dialogAction={
        <>
          <StyledButtonV2
            label="Cancel"
            variant="outlined"
            onClick={() => disclosureHost.close()}
          />
          <StyledButtonV2
            label="Save"
            variant="contained"
            onClick={handleSave}
            sx={{ width: "105px" }}
          />
        </>
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "70vw",
          height: "80vh",
        },
      }}
    >
      <Flex flexDirection="column" gap={2} width="100%" height="100%" pt={2}>
        <Flex justifyContent="flex-end">
          <FormControlLabel
            control={
              <Switch
                checked={markAsNormal}
                onChange={(e) => handleMarkAsNormalChange(e.target.checked)}
                size="small"
              />
            }
            label={<Text fontSize="14px">Mark as Normal</Text>}
          />
        </Flex>

        <CheckboxCardSections
          formik={draft}
          sections={PHYSICAL_ASSESSMENT_EXAM_SECTIONS}
        />

        <CustomTextArea
          label="Overall MSE Note"
          name="overallMseNote"
          placeholder="Summarize overall mental status findings..."
          value={draft.values.overallMseNote || ""}
          onChange={draft.handleChange}
          minRows={3}
        />
      </Flex>
    </StyledFormModal>
  );
};

export default MentalStatusExaminationModal;
